import React, { useState, useEffect, useMemo } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Circle, PieChart } from 'lucide-react'
import menuItemService from '../../services/menuItemService'
import managementService from '../../services/managementService'
import logo from '../../assets/logo.png'
import FooterMenu from './FooterMenu'
import { setFooterNavMode, setSidebarMobileOpen } from '../../store/slices/uiSlice'
import {
  FOOTER_NAV_MODES,
  APPLICATION_MENU_ITEMS,
  CELYNTIQ_CRM_LOGIN_URL
} from '../../config/footerNavConfig'
import './Sidebar.css'

/** Hidden from sidebar UI only — keep config/routes intact */
const HIDDEN_SIDEBAR_MENU_IDS = new Set([
  'development',
  'billing',
  'development-reports',
  'billing-reports',
  // CRM lives in Celyntiq — hide duplicate Masters/Reports hubs (keep in config)
  'crm-masters',
  'crm-reports'
])

const isSidebarMenuVisible = (item) => {
  if (!item) return false
  if (item.hidden) return false
  const id = String(item.id || '').toLowerCase()
  if (HIDDEN_SIDEBAR_MENU_IDS.has(id)) return false
  if (id.includes('development') || id.includes('billing')) return false
  return true
}

const REPORTS_NAV = {
  id: 'reports',
  label: 'Reports',
  icon: PieChart,
  path: FOOTER_NAV_MODES.reports.defaultPath
}

/** CRM menu detection — API menuCode/path may vary; always treat as external Celyntiq */
const isCrmMenuItem = (item) => {
  if (!item) return false
  const id = String(item.id || '').toLowerCase()
  const path = String(item.path || '').toLowerCase().split('?')[0]
  const label = String(item.label || '').toLowerCase().trim()
  if (id === 'crm') return true
  if (path === '/crm') return true
  if (label === 'crm') return true
  return Boolean(item.externalUrl)
}

const resolveExternalUrl = (item) => {
  if (item?.externalUrl) return item.externalUrl
  if (isCrmMenuItem(item)) return CELYNTIQ_CRM_LOGIN_URL
  return null
}

const Sidebar = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { sidebarCollapsed, footerNavMode, sidebarMobileOpen } = useSelector((state) => state.ui)
  const location = useLocation()
  const [applicationMenuItems, setApplicationMenuItems] = useState(APPLICATION_MENU_ITEMS)
  const [hideManagement, setHideManagement] = useState(false)

  useEffect(() => {
    let cancelled = false
    managementService.getAccessCached()
      .then((res) => {
        if (cancelled || !res?.success || !res.data) return
        const { isAdmin, permissions = [] } = res.data
        setHideManagement(!isAdmin && !permissions.includes('VIEW'))
      })
      .catch(() => { /* keep visible; report pages surface access errors */ })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await menuItemService.getAllMenuItems()
        if (cancelled || !res?.success || !Array.isArray(res.data)) return
        const iconMap = Object.fromEntries(
          APPLICATION_MENU_ITEMS.map((item) => [item.id, item.icon])
        )
        const items = res.data
          .filter((item) => item.isActive !== false)
          .filter((item) => !['settings', 'support', 'ai-assistant', 'reports', 'development', 'billing'].includes(item.menuCode))
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((item) => {
            const code = String(item.menuCode || '').toLowerCase()
            const fallback = APPLICATION_MENU_ITEMS.find((a) => a.id === code || a.id === item.menuCode)
            const mapped = {
              id: item.menuCode || item._id,
              label: item.label,
              icon: iconMap[item.menuCode] || iconMap[code] || Circle,
              path: item.path || '#',
              externalUrl: fallback?.externalUrl
            }
            // Force CRM → Celyntiq even when API returns plain /crm path
            if (isCrmMenuItem(mapped) || code === 'crm' || String(item.path || '').toLowerCase() === '/crm') {
              mapped.externalUrl = CELYNTIQ_CRM_LOGIN_URL
              mapped.id = 'crm'
            }
            return mapped
          })
          .filter(isSidebarMenuVisible)
        if (items.length > 0) setApplicationMenuItems(items)
      } catch (err) {
        if (!cancelled) console.warn('Sidebar: could not load menu items, using fallback', err)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const menuItems = useMemo(() => {
    const items =
      footerNavMode === 'applications'
        ? applicationMenuItems
        : FOOTER_NAV_MODES[footerNavMode]?.menuItems || applicationMenuItems
    return (items || [])
      .filter(isSidebarMenuVisible)
      .filter((item) => !(hideManagement && item.id === 'management-reports'))
  }, [footerNavMode, applicationMenuItems, hideManagement])

  const showReportsEntry = footerNavMode === 'applications'
  const sectionLabel =
    footerNavMode !== 'applications'
      ? FOOTER_NAV_MODES[footerNavMode]?.label
      : null

  const isMenuActive = (path, item) => {
    if (!path || path === '#') return false

    const [basePath, queryString] = path.split('?')
    const currentPath = location.pathname
    const currentSearch = new URLSearchParams(location.search)

    // Hub menus: highlight when on any declared child prefix
    const prefixes = item?.activePrefixes
    if (Array.isArray(prefixes) && prefixes.length > 0) {
      return prefixes.some(
        (prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`),
      )
    }

    // Paths with query params — exact pathname + query match only
    if (queryString) {
      if (currentPath !== basePath) return false
      const expected = new URLSearchParams(queryString)
      for (const [key, value] of expected.entries()) {
        if (currentSearch.get(key) !== value) return false
      }
      return true
    }

    if (basePath === '/dashboard') {
      if (currentPath === '/dashboard/ai-analytics' || currentPath.startsWith('/ai-assistant')) return false
      return currentPath === '/dashboard'
    }

    if (basePath === '/ai-assistant') {
      return currentPath === '/ai-assistant' || currentPath.startsWith('/ai-assistant/')
    }

    if (basePath === '/settings') {
      return currentPath === '/settings' && !location.search
    }

    // Deep paths: exact match or nested routes (not used for /support hub — use ?query paths)
    if (basePath === '/support') {
      return currentPath === '/support' && !location.search
    }

    if (currentPath === basePath) return true
    if (currentPath.startsWith(`${basePath}/`)) return true

    return false
  }

  const handleReportsClick = (e) => {
    e.preventDefault()
    dispatch(setFooterNavMode('reports'))
    navigate(REPORTS_NAV.path)
  }

  const handleExternalMenuClick = (e, item) => {
    e.preventDefault()
    e.stopPropagation()
    const url = resolveExternalUrl(item)
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    dispatch(setSidebarMobileOpen(false))
  }

  return (
    <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${sidebarMobileOpen ? 'show' : ''}`}>
      <div className="sidebar-logo">
        <div className="logo-container">
          <img src={logo} alt="Celeris Ventures" className="logo-icon-img" />
          <div className="logo-text-container">
            <span className="logo-text">Celeris Ventures</span>
            <span className="logo-subtitle">AI Powered ERP</span>
          </div>
        </div>
      </div>

      <nav className="sidebar-nav">
        {sectionLabel && (
          <div
            className="sidebar-section-label"
            title={sidebarCollapsed ? sectionLabel : undefined}
          >
            {!sidebarCollapsed && <span>{sectionLabel}</span>}
            {sidebarCollapsed && <span className="sidebar-section-label-dot" aria-hidden="true" />}
          </div>
        )}

        {menuItems.map((item) => {
          const active = isMenuActive(item.path, item)
          const externalUrl = resolveExternalUrl(item)

          if (externalUrl) {
            return (
              <button
                key={item.id}
                type="button"
                className="nav-item"
                title={sidebarCollapsed ? item.label : `${item.label} (opens in new tab)`}
                data-menu={item.id}
                onClick={(e) => handleExternalMenuClick(e, item)}
              >
                <div className="nav-icon">
                  <item.icon size={20} />
                </div>
                <span className="nav-label">{item.label}</span>
              </button>
            )
          }

          return (
            <NavLink
              key={item.id}
              to={item.path}
              end={!!item.path?.includes('?')}
              className={`nav-item ${active ? 'active' : ''}`}
              aria-current={active ? 'page' : undefined}
              title={sidebarCollapsed ? item.label : ''}
              data-menu={item.id}
            >
              <div className="nav-icon">
                <item.icon size={20} />
              </div>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          )
        })}

        {showReportsEntry && (
          <>
            <div className="nav-separator" role="separator" aria-hidden="true" />
            <button
              type="button"
              className="nav-item nav-item-reports"
              onClick={handleReportsClick}
              title={sidebarCollapsed ? REPORTS_NAV.label : ''}
              data-menu={REPORTS_NAV.id}
            >
              <div className="nav-icon">
                <REPORTS_NAV.icon size={20} />
              </div>
              <span className="nav-label">{REPORTS_NAV.label}</span>
            </button>
          </>
        )}
      </nav>

      <FooterMenu />
    </div>
  )
}

export default Sidebar
